// Polished, premium lightbox: contained sizing, smooth enter/exit, backdrop close, swipe, keyboard.
import { useEffect, useState, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

export default function Lightbox({ images, startIndex = 0, open, onClose }) {
  const { t } = useT();
  const [index, setIndex] = useState(startIndex);
  const [phase, setPhase] = useState("closed"); // closed | entering | open | exiting
  const touchStart = useRef(null);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line
      setIndex(startIndex);
      // eslint-disable-next-line
      setPhase("entering");
      const t1 = setTimeout(() => setPhase("open"), 20);
      return () => clearTimeout(t1);
    }
    if (phase === "open" || phase === "entering") {
      // eslint-disable-next-line
      setPhase("exiting");
      const t2 = setTimeout(() => setPhase("closed"), 220);
      return () => clearTimeout(t2);
    }
    return undefined;
  }, [open]); // eslint-disable-line

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, next, prev, onClose]);

  if (phase === "closed") return null;

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchStart.current = null;
  };
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const visible = phase === "open" || phase === "entering";

  return (
    <div
      data-testid="lightbox-overlay"
      onClick={handleBackdrop}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-8 transition-opacity duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      style={{ background: "rgba(15, 20, 26, 0.86)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 md:px-10 py-5 text-white pointer-events-none">
        <p className="text-sm font-semibold tracking-wide tabular-nums bg-white/12 rounded-full px-4 py-1.5 pointer-events-auto">
          {index + 1} <span className="text-white/55 mx-1">/</span> {images.length}
        </p>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          aria-label={t("nav.close")}
          className="pointer-events-auto w-11 h-11 rounded-full bg-white/12 hover:bg-white/25 grid place-items-center transition-all hover:scale-105"
        >
          <X size={18} strokeWidth={2.2} />
        </button>
      </div>

      {/* Image */}
      <div onClick={handleBackdrop} className="relative w-full h-full flex items-center justify-center">
        <img
          src={images[index]}
          alt={`${index + 1} / ${images.length}`}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          className={`rounded-xl shadow-2xl select-none object-contain transition-all duration-300 ${
            visible ? "scale-100" : "scale-95"
          }`}
          style={{ maxWidth: "72vw", maxHeight: "78vh" }}
        />

        <button
          data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label={t("gal.previous")}
          className="hidden md:grid absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 place-items-center text-white transition-all hover:scale-110"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label={t("gal.next")}
          className="hidden md:grid absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 place-items-center text-white transition-all hover:scale-110"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Mobile controls */}
      <div className="md:hidden absolute bottom-6 inset-x-0 flex items-center justify-center gap-4">
        <button onClick={prev} aria-label={t("gal.previous")} className="w-12 h-12 rounded-full bg-white/15 grid place-items-center text-white">
          <ChevronLeft size={20} />
        </button>
        <button onClick={next} aria-label={t("gal.next")} className="w-12 h-12 rounded-full bg-white/15 grid place-items-center text-white">
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
